import React, { createContext, useContext, useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('bonnot_token'));
  const [loading, setLoading] = useState(true);

  // Check token on mount
  useEffect(function() {
    async function checkAuth() {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API}/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (res.ok) {
          const userData = await res.json();
          setUser(userData);
        } else {
          // Token invalid, clear it
          localStorage.removeItem('bonnot_token');
          setToken(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setLoading(false);
      }
    }

    checkAuth();
  }, [token]);

  async function login(email, password) {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    let data;
    try {
      const clonedRes = res.clone();
      data = await clonedRes.json();
    } catch (e) {
      console.error('Login JSON parse error:', e);
      throw new Error('Erreur de connexion au serveur');
    }
    
    if (!res.ok) {
      throw new Error(data.detail || 'Erreur de connexion');
    }

    localStorage.setItem('bonnot_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }

  async function register(userData) {
    const res = await fetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });

    let data;
    try {
      const clonedRes = res.clone();
      data = await clonedRes.json();
    } catch (e) {
      console.error('Register JSON parse error:', e);
      throw new Error('Erreur de connexion au serveur');
    }
    
    if (!res.ok) {
      throw new Error(data.detail || 'Erreur d\'inscription');
    }

    localStorage.setItem('bonnot_token', data.token);
    setToken(data.token);
    setUser(data.user);
    try { if (window.fbq) window.fbq('track', 'CompleteRegistration', { method: 'email' }); if (window.gtag) window.gtag('event', 'sign_up', { method: 'email' }); } catch(e) {}
    return data.user;
  }

  async function logout() {
    if (token) {
      try {
        await fetch(`${API}/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    localStorage.removeItem('bonnot_token');
    setToken(null);
    setUser(null);
  }

  async function updateProfile(updates) {
    const res = await fetch(`${API}/auth/me`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updates)
    });

    if (!res.ok) {
      throw new Error('Erreur de mise à jour');
    }

    const updatedUser = await res.json();
    setUser(updatedUser);
    return updatedUser;
  }

  async function getMyProjects() {
    const res = await fetch(`${API}/auth/my-projects`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!res.ok) {
      const error = new Error(`Erreur de chargement des projets (${res.status})`);
      error.status = res.status;
      if (res.status === 401) {
        error.message = '401 - Session invalide';
      }
      throw error;
    }

    return await res.json();
  }

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateProfile,
    getMyProjects
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export default AuthContext;
